import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/Modules/Core/Components/shadcn/button';
import { navigateBackWithFallback } from '@/Utils/Routes/navigation';

type AppPageProps = React.HTMLAttributes<HTMLDivElement> & {
  contentClassName?: string;
};

type AppPageHeaderProps = {
  breadcrumbs?: Array<{
    label: string;
    to?: string;
  }>;
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  backLink?: string;
  className?: string;
};

type AppPageSectionProps = React.HTMLAttributes<HTMLElement>;

type AppMetricCardProps = {
  label: string;
  value: React.ReactNode;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
};

export function AppPage({
  children,
  className,
  contentClassName,
  ...props
}: AppPageProps) {
  return (
    <div className={cn('app-page', className)} {...props}>
      <div className="app-page-scroll">
        <div className={cn('app-page-container', contentClassName)}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function AppPageHeader({
  breadcrumbs: _breadcrumbs,
  eyebrow,
  title,
  description,
  actions,
  backLink,
  className,
}: AppPageHeaderProps) {
  const navigate = useNavigate();

  const handleGoBack = React.useCallback(() => {
    navigateBackWithFallback(navigate, backLink);
  }, [backLink, navigate]);

  return (
    <header
      className={cn(
        'flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between',
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-3">
        {backLink ? (
          <Button
            type="button"
            variant="outline"
            size="icon-lg"
            className="shrink-0"
            aria-label="Go back"
            onClick={handleGoBack}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        ) : null}

        <div className="min-w-0">
          {eyebrow ? (
            <span className="mb-2.5 block text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              {eyebrow}
            </span>
          ) : null}
          <h1 className="text-[30px] font-medium tracking-tight text-foreground">
            {title}
          </h1>
          {description ? (
            <p className="max-w-3xl text-[13px] leading-6 text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
      </div>

      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </header>
  );
}

export function AppPageSection({
  children,
  className,
  ...props
}: AppPageSectionProps) {
  return (
    <section className={cn('flex min-w-0 flex-col gap-6', className)} {...props}>
      {children}
    </section>
  );
}

export function AppMetricCard({
  label,
  value,
  description,
  icon: Icon,
  className,
}: AppMetricCardProps) {
  return (
    <div
      className={cn(
        'app-metric-card p-5',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            {label}
          </p>
          <div className="text-[30px] font-semibold leading-none text-foreground">
            {value}
          </div>
        </div>

        {Icon ? (
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Icon className="h-4 w-4" />
          </span>
        ) : null}
      </div>

      {description ? (
        <p className="mt-3 text-[12px] leading-[1.45] text-muted-foreground">
          {description}
        </p>
      ) : null}
    </div>
  );
}
